"use client";
import ImageNext from "next/image";
import React, { useState, useRef, useEffect } from "react";
interface CroppedImageProps {
  src: string;
  width: number;
  height: number;
  alt?: string;
  className?: string;
  cropPosition?: "center" | "top" | "bottom" | "left" | "right";
  quality?: number;
  placeholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  loading?:any;
}
// Global Cropped Image Component
const CroppedImage = ({
  src,
  width,
  height,
  alt = "Image",
  className = "",
  cropPosition = "center", // 'center', 'top', 'bottom', 'left', 'right'
  quality = 0.9,
  placeholder = null,
  onLoad = () => {},
  onError = () => {},
  loading,
  ...props
}: CroppedImageProps) => {
  const [croppedSrc, setCroppedSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Cleanup function for object URLs
  useEffect(() => {
    return () => {
      if (croppedSrc && croppedSrc.startsWith("blob:")) {
        URL.revokeObjectURL(croppedSrc);
      }
    };
  }, [croppedSrc]);
  // Main cropping logic
  useEffect(() => {
    if (!src || !width || !height) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const cropImage = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            // Set canvas to exact target dimensions
            canvas.width = width;
            canvas.height = height;
            // Calculate aspect ratios
            const targetRatio = width / height;
            const sourceRatio = img.width / img.height;
            let sourceX = 0;
            let sourceY = 0;
            let sourceWidth = img.width;
            let sourceHeight = img.height;
            // Calculate crop area to maintain target aspect ratio
            if (sourceRatio > targetRatio) {
              // Image is wider - crop horizontally
              sourceWidth = img.height * targetRatio;
              switch (cropPosition) {
                case "left":
                  sourceX = 0;
                  break;
                case "right":
                  sourceX = img.width - sourceWidth;
                  break;
                case "center":
                default:
                  sourceX = (img.width - sourceWidth) / 2;
                  break;
              }
            } else if (sourceRatio < targetRatio) {
              // Image is taller - crop vertically
              sourceHeight = img.width / targetRatio;
              switch (cropPosition) {
                case "top":
                  sourceY = 0;
                  break;
                case "bottom":
                  sourceY = img.height - sourceHeight;
                  break;
                case "center":
                default:
                  sourceY = (img.height - sourceHeight) / 2;
                  break;
              }
            }
            // Draw the cropped and resized image
            if (ctx) {
              ctx.drawImage(
                img,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight, // Source
                0,
                0,
                width,
                height // Destination
              );
            } else {
              reject(new Error("Failed to get canvas context"));
              return;
            }
            // Convert to blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  resolve(url);
                } else {
                  reject(new Error("Failed to create blob"));
                }
              },
              "image/webp",
              quality
            );
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = src;
      });
    };
    cropImage()
      .then((croppedUrl) => {
        setCroppedSrc(croppedUrl as string);
        setIsLoading(false);
        onLoad();
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
        onError(err);
      });
  }, [src, width, height, cropPosition, quality]);
  // Loading state
  if (isLoading) {
    return (
      <div
        className={`bg-gray-200 animate-pulse flex items-center justify-center w-full h-full ${className}`}
        {...props}
      >
        {placeholder || <div className="text-gray-400 text-sm">Loading...</div>}
      </div>
    );
  }
  // Error state
  if (error || !croppedSrc) {
    return (
      <div
        className={`bg-gray-100 border border-gray-300 flex items-center justify-center w-full h-full ${className}`}
        {...props}
      >
        <div className="text-gray-500 text-sm text-center p-2">
          Failed to load image
        </div>
      </div>
    );
  }
  // Rendered image
  return (
    <ImageNext
      src={croppedSrc}
      alt={alt}
      width={width}
      height={height}
      className={`${className} w-full object-cover`}
      {...props}
      loading={loading}
    />
  );
};
export default CroppedImage;